import React, { useState, useMemo } from 'react';
import {
  ArrowLeft, Building2, FileText, ExternalLink, ChevronDown, ChevronUp,
  Package, Mail, Phone, Globe, MapPin, Hash, CreditCard, StickyNote, Receipt, ClipboardList, Loader2, Edit2, Save,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { base44 } from '@/api/base44Client';
import PortalUsersSection from './PortalUsersSection';
import FulfilmentStageControls from './FulfilmentStageControls';
import SquareStockPanel from './SquareStockPanel';
import SquareSyncTag from './SquareSyncTag';
import { sortB2BOrders, fmtB2BOrderDate } from '@/lib/b2bOrderDate';

// Full account detail view shown when an admin clicks a trade account.
// Surfaces everything tied to the account: contact + financial summary, every
// B2B order (with expandable line items), generated invoice downloads, and
// the internal memo / notes — so the full account history is in one place.

const STATUS_COLORS = {
  draft: 'text-white/40 border-white/15',
  acknowledged: 'text-blue-400 border-blue-400/30',
  in_production: 'text-yellow-400 border-yellow-400/30',
  stock_allocated: 'text-cyan-400 border-cyan-400/30',
  dispatched: 'text-purple-400 border-purple-400/30',
  invoiced: 'text-orange-400 border-orange-400/30',
  paid: 'text-green-400 border-green-400/30',
  cancelled: 'text-red-400 border-red-400/30',
  unallocated: 'text-yellow-400 border-yellow-400/30',
};

function fmt(n) {
  const v = Number(n) || 0;
  return `£${v.toFixed(2)}`;
}

export default function AccountDetailPanel({ account, orders, onBack, onAccountUpdated, onOrderUpdated }) {
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [downloadingPickList, setDownloadingPickList] = useState(null);
  const [editingFinancials, setEditingFinancials] = useState(false);
  const [savingFinancials, setSavingFinancials] = useState(false);
  const [financialForm, setFinancialForm] = useState({});
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const { toast } = useToast();

  const startEditFinancials = () => {
    setFinancialForm({
      payment_terms_days: account.payment_terms_days ?? null,
      credit_limit_gbp: account.credit_limit_gbp ?? null,
      discount_percent: account.discount_percent ?? 0,
      balance: account.balance ?? 0,
    });
    setEditingFinancials(true);
  };

  const saveFinancials = async () => {
    setSavingFinancials(true);
    try {
      const payload = {
        payment_terms_days: financialForm.payment_terms_days === '' || financialForm.payment_terms_days === null ? null : Number(financialForm.payment_terms_days),
        credit_limit_gbp: financialForm.credit_limit_gbp === '' || financialForm.credit_limit_gbp === null ? null : Number(financialForm.credit_limit_gbp),
        discount_percent: financialForm.discount_percent === '' || financialForm.discount_percent === null ? 0 : Number(financialForm.discount_percent),
        balance: financialForm.balance === '' || financialForm.balance === null ? 0 : Number(financialForm.balance),
      };
      await base44.entities.B2BAccount.update(account.id, payload);
      onAccountUpdated({ ...account, ...payload });
      setEditingFinancials(false);
      toast({ title: 'Financial terms updated' });
    } catch (e) {
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    }
    setSavingFinancials(false);
  };

  const handleDownloadPickList = async (orderId, poNumber) => {
    setDownloadingPickList(orderId);
    try {
      const result = await base44.functions.invoke('generateB2BPickingSlip', { order_id: orderId });
      if (!result?.data?.success || !result.data.pdf_url) {
        throw new Error(result?.data?.error || 'Could not generate picking slip');
      }
      // Fetch the public PDF URL as a blob so the browser downloads it
      // (rather than opening the in-browser PDF viewer).
      const res = await fetch(result.data.pdf_url);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.data.filename || `picking-slip-${poNumber || orderId.slice(-8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: e?.message || 'Could not download picking slip', variant: 'destructive' });
    }
    setDownloadingPickList(null);
  };

  // Generate (or regenerate) this order's VAT invoice without leaving the
  // account view — same behaviour as the Purchase Orders list: assigns the
  // next number in the single system-wide sequence, renders the PDF, saves
  // it back onto the order and moves the status to 'invoiced'.
  const handleGenerateInvoice = async (orderId) => {
    setGeneratingInvoice(orderId);
    try {
      const res = await base44.functions.invoke('b2bGenerateInvoice', { order_id: orderId });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Invoice generation failed');
      onOrderUpdated?.({
        ...(res.data.order || {}),
        id: orderId,
        invoice_url: res.data.invoice_url,
        invoice_number: res.data.invoice_number,
        status: 'invoiced',
      });
      const warnings = res.data.square?.warnings || [];
      toast(warnings.length
        ? { title: 'Invoice generated — stock warning', description: warnings[0] }
        : { title: 'Invoice generated', description: res.data.invoice_number });
    } catch (e) {
      toast({ title: 'Invoice failed', description: e?.message || 'Unknown error', variant: 'destructive' });
    }
    setGeneratingInvoice(null);
  };

  const accountOrders = useMemo(
    () => sortB2BOrders((orders || []).filter((o) => o.b2b_account_id === account.id || o.company_name === account.company_name)),
    [orders, account.id, account.company_name]
  );

  const invoicedOrders = accountOrders.filter((o) => o.invoice_url);
  const totalOrderValue = accountOrders.reduce((s, o) => s + (o.total_inc_vat || 0), 0);
  const outstandingBalance = accountOrders
    .filter((o) => !['paid', 'cancelled'].includes(o.status))
    .reduce((s, o) => s + (o.total_inc_vat || 0), 0);

  const Info = ({ icon: Icon, label, value, href }) => (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-white/30 text-[9px] font-bold tracking-widest">{label}</p>
        {href ? (
          <a href={href} className="text-white/70 text-xs hover:text-primary transition-colors break-all">{value}</a>
        ) : (
          <p className="text-white/70 text-xs break-words">{value || '—'}</p>
        )}
      </div>
    </div>
  );

  const FinancialInput = ({ icon: Icon, label, value, onChange, type }) => (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-white/30 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-white/30 text-[9px] font-bold tracking-widest">{label}</p>
        <input
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          className="seo-input w-24 mt-0.5"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Back */}
      <button onClick={onBack} className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-widest transition-colors">
        <ArrowLeft className="w-4 h-4" /> BACK TO ACCOUNTS
      </button>

      {/* Header */}
      <div className="border border-white/10 bg-[#111115] p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 bg-primary/10 flex items-center justify-center shrink-0 text-primary">
            <Building2 className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-white font-black text-xl">{account.company_name}</h2>
              {account.account_code && <span className="text-white/40 text-xs font-mono border border-white/15 px-2 py-0.5">{account.account_code}</span>}
              {!account.active && <span className="text-red-400 text-[10px] font-bold tracking-widest">INACTIVE</span>}
            </div>
            <div className="flex flex-wrap gap-5 mt-3">
              <Info icon={Mail} label="EMAIL" value={account.contact_email} href={account.contact_email ? `mailto:${account.contact_email}` : null} />
              <Info icon={Phone} label="PHONE" value={account.contact_phone} href={account.contact_phone ? `tel:${account.contact_phone}` : null} />
              <Info icon={Globe} label="WEBSITE" value={account.website} href={account.website || null} />
              <Info icon={MapPin} label="ADDRESS" value={[account.address_line1, account.address_city, account.address_postcode].filter(Boolean).join(', ')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 min-w-[260px]">
            <div className="border border-white/10 bg-[#0d0d0f] p-3 text-center">
              <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">ORDERS</p>
              <p className="text-white font-black text-lg">{accountOrders.length}</p>
            </div>
            <div className="border border-white/10 bg-[#0d0d0f] p-3 text-center">
              <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">INVOICES</p>
              <p className="text-white font-black text-lg">{invoicedOrders.length}</p>
            </div>
            <div className="border border-green-400/20 bg-green-400/[0.03] p-3 text-center">
              <p className="text-green-400/70 text-[9px] font-bold tracking-widest mb-1">ORDER VALUE</p>
              <p className="text-green-400 font-black text-sm">{fmt(totalOrderValue)}</p>
            </div>
            <div className="border border-yellow-400/20 bg-yellow-400/[0.03] p-3 text-center">
              <p className="text-yellow-400/70 text-[9px] font-bold tracking-widest mb-1">OUTSTANDING</p>
              <p className="text-yellow-400 font-black text-sm">{fmt(outstandingBalance)}</p>
            </div>
          </div>
        </div>

        {/* Financial terms strip */}
        <div className="flex flex-wrap gap-5 mt-4 pt-4 border-t border-white/10">
          <Info icon={Hash} label="VAT NUMBER" value={account.vat_number} />
          <Info icon={Hash} label="COMPANY REG" value={account.company_reg} />
          <Info icon={Hash} label="D-U-N-S" value={account.duns_number} />
          {editingFinancials ? (
            <FinancialInput icon={CreditCard} label="PAYMENT TERMS (DAYS)" value={financialForm.payment_terms_days} onChange={(v) => setFinancialForm(p => ({ ...p, payment_terms_days: v }))} type="number" />
          ) : (
            <Info icon={CreditCard} label="PAYMENT TERMS" value={account.payment_terms_days ? `Net ${account.payment_terms_days} days` : '—'} />
          )}
          {editingFinancials ? (
            <FinancialInput icon={CreditCard} label="CREDIT LIMIT (£)" value={financialForm.credit_limit_gbp} onChange={(v) => setFinancialForm(p => ({ ...p, credit_limit_gbp: v }))} type="number" />
          ) : (
            <Info icon={CreditCard} label="CREDIT LIMIT" value={account.credit_limit_gbp != null ? fmt(account.credit_limit_gbp) : '—'} />
          )}
          {editingFinancials ? (
            <FinancialInput icon={CreditCard} label="TRADE DISCOUNT (%)" value={financialForm.discount_percent} onChange={(v) => setFinancialForm(p => ({ ...p, discount_percent: v }))} type="number" />
          ) : (
            <Info icon={CreditCard} label="TRADE DISCOUNT" value={Number(account.discount_percent) > 0 ? `${account.discount_percent}% off parts` : 'None'} />
          )}
          {editingFinancials ? (
            <FinancialInput icon={CreditCard} label="BALANCE (£)" value={financialForm.balance} onChange={(v) => setFinancialForm(p => ({ ...p, balance: v }))} type="number" />
          ) : (
            <Info icon={CreditCard} label="BALANCE" value={account.balance != null ? fmt(account.balance) : '—'} />
          )}
          <div className="flex items-center gap-2 ml-auto">
            {editingFinancials ? (
              <>
                <button onClick={saveFinancials} disabled={savingFinancials} className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors">
                  {savingFinancials ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} SAVE
                </button>
                <button onClick={() => setEditingFinancials(false)} className="border border-white/15 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors">
                  CANCEL
                </button>
              </>
            ) : (
              <button onClick={startEditFinancials} className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors">
                <Edit2 className="w-3.5 h-3.5" /> EDIT
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Portal Users — manage who can access the trade portal */}
      <PortalUsersSection account={account} onAccountUpdated={onAccountUpdated} />

      {/* Orders */}
      <div>
        <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3 flex items-center gap-2">
          <Package className="w-3.5 h-3.5" /> ORDERS &amp; INVOICES ({accountOrders.length})
        </p>
        {accountOrders.length === 0 ? (
          <div className="border border-white/10 bg-[#111115] p-8 text-center text-white/30">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs font-bold tracking-widest">NO ORDERS YET</p>
            <p className="text-[11px] mt-1">Upload a purchase order to create this account's first order</p>
          </div>
        ) : (
          <div className="space-y-2">
            {accountOrders.map((order) => {
              const totalLines = (order.line_items || []).length;
              const allocatedLines = (order.line_items || []).filter((li) => li.stock_allocated).length;
              const isOpen = expandedOrder === order.id;
              const syncByIndex = new Map((order.square_stock_deduction || []).map((r) => [r.line_index, r]));
              return (
                <div key={order.id} className="border border-white/10 bg-[#111115]">
                  <div
                    className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                    onClick={() => setExpandedOrder(isOpen ? null : order.id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-2 py-0.5 border text-[10px] font-bold tracking-widest uppercase ${STATUS_COLORS[order.status] || 'text-white/40 border-white/15'}`}>{(order.status || 'draft').replace('_', ' ')}</span>
                        {order.po_number && <span className="text-white/40 text-xs font-mono">PO {order.po_number}</span>}
                        {order.invoice_number && <span className="text-orange-400/80 text-[10px] font-mono">{order.invoice_number}</span>}
                      </div>
                      <p className="text-white/30 text-xs mt-1">
                        {fmtB2BOrderDate(order)} · {totalLines} item{totalLines !== 1 ? 's' : ''} · {fmt(order.subtotal_ex_vat || 0)} ex. VAT
                        {allocatedLines > 0 && <span className="ml-2 text-cyan-400">{allocatedLines}/{totalLines} allocated</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPickList(order.id, order.po_number); }}
                        disabled={downloadingPickList === order.id}
                        className="inline-flex items-center gap-1.5 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400 font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {downloadingPickList === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />} PICK LIST
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleGenerateInvoice(order.id); }}
                        disabled={generatingInvoice === order.id}
                        title={order.invoice_url ? "Regenerate this order's invoice" : "Generate this order's invoice"}
                        className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
                      >
                        {generatingInvoice === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Receipt className="w-3.5 h-3.5" />}
                        {order.invoice_url ? 'REGENERATE INVOICE' : 'GENERATE INVOICE'}
                      </button>
                      {order.invoice_url && (
                        <a
                          href={order.invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 border border-orange-400/30 bg-orange-400/10 hover:bg-orange-400 hover:text-black text-orange-400 font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors"
                        >
                          <FileText className="w-3.5 h-3.5" /> INVOICE
                        </a>
                      )}
                      {order.source_pdf_url && (
                        <a
                          href={order.source_pdf_url}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> PO
                        </a>
                      )}
                      {isOpen ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="border-t border-white/10 p-5 space-y-4">
                      <SquareStockPanel order={order} />
                      <FulfilmentStageControls order={order} onStageAdvanced={onOrderUpdated} />
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="text-white/30 text-left py-2 pr-3 w-8">#</th>
                              <th className="text-white/30 text-left py-2 pr-3">Code</th>
                              <th className="text-white/30 text-left py-2 pr-3">Description</th>
                              <th className="text-white/30 text-right py-2 pr-3">Qty</th>
                              <th className="text-white/30 text-right py-2 pr-3">Unit Price</th>
                              <th className="text-white/30 text-right py-2">Ext. Price</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(order.line_items || []).map((li, idx) => (
                              <tr key={idx} className={`border-b border-white/5 ${li.stock_allocated ? 'bg-cyan-400/5' : ''}`}>
                                <td className="text-white/40 py-2 pr-3">{li.item_no}</td>
                                <td className="text-white/70 font-mono py-2 pr-3">{li.product_code}</td>
                                <td className="text-white py-2 pr-3">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span>{li.description}</span>
                                    <SquareSyncTag result={syncByIndex.get(idx)} />
                                  </div>
                                </td>
                                <td className="text-white text-right py-2 pr-3">{li.qty}</td>
                                <td className="text-white/70 text-right py-2 pr-3">{fmt(li.unit_price)}</td>
                                <td className="text-white font-bold text-right py-2">{fmt(li.extended_price)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-white/20">
                              <td colSpan={5} className="text-right text-white/50 text-xs py-2 pr-3 font-bold">Subtotal ex. VAT</td>
                              <td className="text-right text-white font-bold text-xs py-2 pr-3">{fmt(order.subtotal_ex_vat)}</td>
                            </tr>
                            <tr>
                              <td colSpan={5} className="text-right text-white/50 text-xs py-1 pr-3">VAT (20%)</td>
                              <td className="text-right text-white/70 text-xs py-1 pr-3">{fmt(order.vat_amount)}</td>
                            </tr>
                            <tr>
                              <td colSpan={5} className="text-right text-white/50 text-xs py-1 pr-3 font-bold">Total inc. VAT</td>
                              <td className="text-right text-white font-bold py-1 pr-3">{fmt(order.total_inc_vat)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {order.delivery_address && (
                        <div>
                          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">DELIVERY ADDRESS</p>
                          <p className="text-white/60 text-xs whitespace-pre-line">{order.delivery_address}</p>
                        </div>
                      )}
                      {order.notes && (
                        <div>
                          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">ORDER NOTES</p>
                          <p className="text-white/60 text-xs whitespace-pre-line">{order.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Invoices list */}
      {invoicedOrders.length > 0 && (
        <div>
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3 flex items-center gap-2">
            <Receipt className="w-3.5 h-3.5" /> INVOICES ({invoicedOrders.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {invoicedOrders.map((o) => (
              <a key={o.id} href={o.invoice_url} target="_blank" rel="noreferrer"
                className="flex items-center gap-3 border border-white/10 bg-[#111115] hover:border-orange-400/30 hover:bg-orange-400/[0.02] p-3 transition-colors">
                <FileText className="w-4 h-4 text-orange-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-xs">{o.invoice_number}</p>
                  <p className="text-white/40 text-[11px]">PO {o.po_number || '—'} · {fmt(o.total_inc_vat)}</p>
                </div>
                <ExternalLink className="w-3.5 h-3.5 text-white/30" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Notes / memo */}
      <div>
        <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3 flex items-center gap-2">
          <StickyNote className="w-3.5 h-3.5" /> NOTES &amp; MEMO
        </p>
        <div className="border border-white/10 bg-[#111115] p-4">
          {account.notes ? (
            <p className="text-white/60 text-xs whitespace-pre-line">{account.notes}</p>
          ) : (
            <p className="text-white/30 text-xs">No internal notes recorded for this account.</p>
          )}
        </div>
      </div>
    </div>
  );
}